import { useState } from 'preact/hooks';
import { LogoMark, ThumbDownIcon, ThumbUpIcon } from './icons';

export type Rating = 1 | -1;

/**
 * A single chat turn. Assistant turns sit in a row with the AI mark on the
 * left; consecutive assistant turns are grouped (avatar only on the first,
 * a spacer keeps the rest aligned). User turns are right-aligned, no avatar.
 */
export function Message({
  role,
  content,
  showAvatar = true,
  grouped = false,
  messageId,
  onRate
}: {
  role: 'user' | 'assistant';
  content: string;
  showAvatar?: boolean;
  grouped?: boolean;
  /** Present only on rateable bot turns from this session. */
  messageId?: string;
  onRate?: (messageId: string, rating: Rating) => Promise<boolean>;
}) {
  if (role === 'user') {
    return (
      <div class="msg-row user">
        <div class="bubble user">{content}</div>
      </div>
    );
  }
  return (
    <div class={`msg-row assistant${grouped ? ' grouped' : ''}`}>
      <div class={`msg-avatar${showAvatar ? '' : ' spacer'}`} aria-hidden="true">
        {showAvatar && <LogoMark />}
      </div>
      <div class="bubble-col">
        <div class="bubble assistant">{content}</div>
        {messageId && onRate && <Feedback messageId={messageId} onRate={onRate} />}
      </div>
    </div>
  );
}

/**
 * Thumbs on a bot turn (spec §7.3). Optimistic: the button fills immediately
 * and only rolls back if the request fails, because a rating is a courtesy and
 * a spinner would draw more attention than the action deserves.
 */
function Feedback({
  messageId,
  onRate
}: {
  messageId: string;
  onRate: (messageId: string, rating: Rating) => Promise<boolean>;
}) {
  const [rating, setRating] = useState<Rating | null>(null);

  async function pick(next: Rating) {
    const previous = rating;
    // Clicking the active thumb clears it locally, but there is no delete
    // endpoint, so we keep the recorded vote and just re-assert it.
    if (previous === next) return;
    setRating(next);
    const ok = await onRate(messageId, next);
    if (!ok) setRating(previous);
  }

  return (
    <div class="feedback" role="group" aria-label="Was this answer helpful?">
      <button
        type="button"
        class={`feedback-btn${rating === 1 ? ' active' : ''}`}
        aria-label="Helpful"
        aria-pressed={rating === 1}
        onClick={() => pick(1)}
      >
        <ThumbUpIcon />
      </button>
      <button
        type="button"
        class={`feedback-btn${rating === -1 ? ' active' : ''}`}
        aria-label="Not helpful"
        aria-pressed={rating === -1}
        onClick={() => pick(-1)}
      >
        <ThumbDownIcon />
      </button>
      {rating !== null && <span class="feedback-thanks">Thanks</span>}
    </div>
  );
}
